import type { ProductConfId, ProductDetail } from '@eliadmin/core/src/api/proform/proform-types';
import type { ProformEdgeSide } from './proform-edge-material';
import type { EnhancedProductConf } from '@eliadmin/core/src/stores/configurator-store';
import {
  extractDataFromField,
  extractProformArray,
  getProformImageUrl,
  parseProformNumber,
} from '@eliadmin/core/src/utils/proform-utils';

export type ProformModelFormat = 'obj' | '3ds';

export type ProformModelSource = {
  format: ProformModelFormat;
  url: string;
};

export type ProformHandleSource = {
  format: 'stl';
  url: string;
};

export type ProformHandleDescriptor = {
  code: string;
  color: string;
  id: string;
  label: string;
  lengthMm: number;
  lengthMode: 'fit-edge' | 'native';
  mountingSeatYMm: number;
  nativeSizeMm: {
    length: number;
    profileDepth: number;
    profileHeight: number;
  };
  placement: 'top-center';
  source: ProformHandleSource;
};

export type ProformEdgeDescriptor = {
  code: string;
  label: string;
  previewUrl?: string;
  textureUrl?: string;
  color?: string;
};

export type ProformModelDescriptor = {
  dimensionPattern?: 'w030-p' | 'w030-xmf8';
  /** Known outer edge profile in native model mm; never inferred from door size. */
  edgeRadiusMm?: number;
  id: string;
  label: string;
  nativeSizeMm: {
    width: number;
    height: number;
    depth: number;
  };
  /**
   * Relative path of the option thumbnail returned by Proform. The web model
   * is looked up beside that image under the same basename with an .obj
   * extension. `sources` remain local fallbacks while the FTP model is being
   * rolled out.
   */
  previewImagePath?: string;
  sources: ProformModelSource[];
  variantCode: string;
};

export type Proform2dPreviewMode = 'model-front' | 'pattern';

export function getProform2dPreviewMode(model: ProformModelDescriptor): Proform2dPreviewMode {
  return getProformModelSources(model).length > 0 ? 'model-front' : 'pattern';
}

export type ProformPreviewAssetSet = {
  configurationMappings: ProformPreviewConfigurationMapping[];
  defaultVariantCode: string;
  edgeRadiusMm?: number;
  handles?: ProformHandleDescriptor[];
  productCode: string;
  variants: ProformModelDescriptor[];
};

export type ProformPreviewConfigurationMapping = {
  executionCode: string;
  executionGroupCode: string;
  modelVariantCode: string;
  usesFilling: boolean;
};

export type ProformPreviewConfiguration = {
  depthMm: number;
  edges: Partial<Record<ProformEdgeSide, ProformEdgeDescriptor>>;
  executionCode: string;
  executionGroupCode: string;
  executionGroupLabel: string;
  executionLabel: string;
  fillingCode?: string;
  fillingGroupCode?: string;
  fillingGroupLabel?: string;
  fillingLabel?: string;
  fillingOrientation?: string;
  fillingOrnamentSide?: string;
  fillingColor?: string;
  /** Selection thumbnail for UI context only; it is not a renderable material texture. */
  fillingPreviewUrl?: string;
  finishCode: string;
  finishColor?: string;
  finishLabel: string;
  finishPreviewUrl?: string;
  finishRotationRadians: number;
  finishTextureUrl?: string;
  handle?: ProformHandleDescriptor;
  hasConfiguredHandleSelection: boolean;
  heightMm: number;
  model: ProformModelDescriptor;
  /** The selected execution accepts glass, even before a filling has been chosen. */
  supportsGlass: boolean;
  /** The selected option is real glass and can use the glass preview layer. */
  usesFilling: boolean;
  variantCode: string;
  widthMm: number;
};

/**
 * Temporary local replacement for the future product-model API response.
 * Keeping the hardcoded data behind this descriptor lets us swap the source
 * for an API call without changing the 2D/3D preview components.
 */
const PRODUCT_PREVIEW_ASSETS: Record<string, ProformPreviewAssetSet> = {
  NDW030: {
    configurationMappings: [
      {
        executionCode: 'P',
        executionGroupCode: 'P',
        modelVariantCode: 'P',
        usesFilling: false,
      },
      {
        executionCode: 'XMF8',
        executionGroupCode: 'M',
        modelVariantCode: 'XMF8',
        usesFilling: true,
      },
    ],
    defaultVariantCode: 'P',
    productCode: 'NDW030',
    variants: [
      {
        dimensionPattern: 'w030-p',
        id: 'NDW030-P',
        label: 'Dvířka W030 · P',
        nativeSizeMm: { width: 399.999, height: 580.054, depth: 18.007 },
        previewImagePath: 'prov/NDW030/prov_NDW030_P.jpg',
        sources: [
          { format: 'obj', url: '/models/proform/w030/Proform_W030_P.obj' },
          { format: '3ds', url: '/models/proform/w030/Proform_W030_P.3ds' },
        ],
        variantCode: 'P',
      },
      {
        dimensionPattern: 'w030-xmf8',
        id: 'NDW030-XMF8',
        label: 'Dvířka W030 · XMF8',
        nativeSizeMm: { width: 396.835, height: 1312.815, depth: 18.061 },
        previewImagePath: 'prov/NDW030/prov_NDW030_XMF8.jpg',
        sources: [
          { format: 'obj', url: '/models/proform/w030/Proform_W030_CS8.obj' },
          { format: '3ds', url: '/models/proform/w030/Proform_W030_CS8.3ds' },
        ],
        variantCode: 'XMF8',
      },
    ],
  },
  NDALVICZENIT: {
    configurationMappings: [],
    defaultVariantCode: 'P',
    edgeRadiusMm: 1,
    // The supplier's UAL-8/5 example has the same curved finger grip as the
    // supplied NOVY files, unlike the straight UAL-8 option's thumbnail.
    handles: [
      {
        code: 'UAL-8/5',
        color: '#aeb4b8',
        id: 'UAL-8-5-250',
        label: 'Úchytka UAL-8/5 · 250 mm',
        lengthMm: 250,
        lengthMode: 'fit-edge',
        mountingSeatYMm: -3,
        nativeSizeMm: { length: 250, profileDepth: 37, profileHeight: 11 },
        placement: 'top-center',
        source: { format: 'stl', url: '/models/proform/handles/ual-8/ual-8-250mm.stl' },
      },
      {
        code: 'UAL-8/5',
        color: '#aeb4b8',
        id: 'UAL-8-5-500',
        label: 'Úchytka UAL-8/5 · 500 mm',
        lengthMm: 500,
        lengthMode: 'fit-edge',
        mountingSeatYMm: -3,
        nativeSizeMm: { length: 500, profileDepth: 37, profileHeight: 11 },
        placement: 'top-center',
        source: { format: 'stl', url: '/models/proform/handles/ual-8/ual-8-500mm.stl' },
      },
      {
        code: 'UAL-8/5',
        color: '#aeb4b8',
        id: 'UAL-8-5-1200',
        label: 'Úchytka UAL-8/5 · 1200 mm',
        lengthMm: 1200,
        lengthMode: 'fit-edge',
        mountingSeatYMm: -3,
        nativeSizeMm: { length: 1200, profileDepth: 37, profileHeight: 11 },
        placement: 'top-center',
        source: { format: 'stl', url: '/models/proform/handles/ual-8/ual-8-1200mm.stl' },
      },
    ],
    productCode: 'NDALVICZENIT',
    variants: [
      {
        id: 'NDALVICZENIT-P',
        edgeRadiusMm: 1,
        label: 'Dvířka ALVIC ZÉNIT · P',
        nativeSizeMm: { width: 596, height: 720, depth: 18 },
        previewImagePath: 'prov/NDALVICZENIT/prov_NDALVICZENIT_P.jpg',
        sources: [],
        variantCode: 'P',
      },
    ],
  },
};

/**
 * Product-specific entries above contain only exceptional local fallbacks or
 * demo accessories. Every other configurable door gets a lightweight runtime
 * asset set once its selected execution supplies a Proform thumbnail. This
 * keeps the shared thumbnail -> sibling OBJ convention independent of W030.
 */
const GENERIC_ASSET_SET_CACHE = new Map<string, ProformPreviewAssetSet>();

const MODEL_PREVIEW_PATH_CACHE = new WeakMap<
  ProformModelDescriptor,
  Map<string, ProformModelDescriptor>
>();
const DYNAMIC_MODEL_CACHE = new WeakMap<
  ProformPreviewAssetSet,
  Map<string, ProformModelDescriptor>
>();

const positiveNumber = (value: string | number | null | undefined, fallback: number) => {
  const parsed = parseProformNumber(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

function getSelectedOption(
  productConf: EnhancedProductConf | null | undefined,
  column: 'user_grp_prov' | 'user_grp_skla' | 'user_kod_folie' | 'user_kod_prov' | 'user_kod_skla',
  value: string,
  groupCode?: string,
) {
  if (!productConf?.root) return undefined;

  const normalizedValue = normalizeCode(value);
  const normalizedGroupCode = normalizeCode(groupCode);
  const matchingOptions = (extractDataFromField(column, productConf) ?? []).filter(
    option => normalizeCode(option.kod) === normalizedValue,
  );

  if (normalizedGroupCode) {
    return matchingOptions.find(option => normalizeCode(option.kod_grp) === normalizedGroupCode);
  }

  // Some products expose Provedení dvířek without a separate group field.
  // Only accept that shape when the selected execution code is unambiguous.
  return matchingOptions.length === 1 ? matchingOptions[0] : undefined;
}

export function getProformTextureProxyUrl(relativePath: string) {
  return `/api/proform-preview-texture?path=${encodeURIComponent(relativePath)}`;
}

const PROFORM_FILES_BASE_URL = new URL('https://apieshop.smako.cz/files/data/');
const PROFORM_PREVIEW_IMAGE_EXTENSION = /\.(?:avif|gif|jpe?g|png|svg|webp)$/i;

function getProformModelProxyUrl(relativePath: string) {
  return `/api/proform-preview-model?path=${encodeURIComponent(relativePath)}`;
}

/**
 * Maps an option thumbnail from the Proform file library to its sibling OBJ.
 * Example: prov/NDW030/prov_NDW030_P.jpg -> prov/NDW030/prov_NDW030_P.obj
 */
export function deriveProformObjPath(previewImagePath: string | null | undefined) {
  const normalizedPath = previewImagePath?.trim().replaceAll('\\', '/');
  if (!normalizedPath || normalizedPath.includes('\0')) return null;

  let previewUrl: URL;
  try {
    if (/^https?:\/\//i.test(normalizedPath)) {
      previewUrl = new URL(normalizedPath);
    } else if (normalizedPath.startsWith('//')) {
      previewUrl = new URL(`https:${normalizedPath}`);
    } else if (normalizedPath.startsWith(PROFORM_FILES_BASE_URL.pathname)) {
      previewUrl = new URL(normalizedPath, PROFORM_FILES_BASE_URL.origin);
    } else {
      previewUrl = new URL(normalizedPath.replace(/^\/+/, ''), PROFORM_FILES_BASE_URL);
    }
  } catch {
    return null;
  }

  if (
    previewUrl.protocol !== 'https:' ||
    previewUrl.origin !== PROFORM_FILES_BASE_URL.origin ||
    !previewUrl.pathname.startsWith(PROFORM_FILES_BASE_URL.pathname) ||
    !PROFORM_PREVIEW_IMAGE_EXTENSION.test(previewUrl.pathname)
  ) {
    return null;
  }

  previewUrl.pathname = previewUrl.pathname.replace(PROFORM_PREVIEW_IMAGE_EXTENSION, '.obj');
  previewUrl.hash = '';
  return `${previewUrl.pathname.slice(PROFORM_FILES_BASE_URL.pathname.length)}${previewUrl.search}`;
}

export function getProformModelSources(model: ProformModelDescriptor) {
  const remoteObjPath = deriveProformObjPath(model.previewImagePath);
  if (!remoteObjPath) return model.sources;

  const remoteSource: ProformModelSource = {
    format: 'obj',
    url: getProformModelProxyUrl(remoteObjPath),
  };

  return [remoteSource, ...model.sources.filter(source => source.url !== remoteSource.url)];
}

function withPreviewImagePath(model: ProformModelDescriptor, previewImagePath: string | undefined) {
  if (!previewImagePath || previewImagePath === model.previewImagePath) return model;

  let cachedModels = MODEL_PREVIEW_PATH_CACHE.get(model);
  if (!cachedModels) {
    cachedModels = new Map();
    MODEL_PREVIEW_PATH_CACHE.set(model, cachedModels);
  }

  const cachedModel = cachedModels.get(previewImagePath);
  if (cachedModel) return cachedModel;

  const resolvedModel = { ...model, previewImagePath };
  cachedModels.set(previewImagePath, resolvedModel);
  return resolvedModel;
}

function normalizeHexColor(value: string | undefined) {
  const normalized = value?.trim().replace(/^#/, '');
  return normalized && /^[0-9a-f]{6}$/i.test(normalized) ? `#${normalized}` : undefined;
}

function normalizeCode(value: string | null | undefined) {
  return value?.trim().toUpperCase() ?? '';
}

const GLASS_EXECUTION_FLAGS = new Set(['1', '3', '4', '5']);
const NON_GLASS_FILLING_GROUPS = new Set(['MRIZ', 'MRIZLAK']);

function getConfigurationMapping(assetSet: ProformPreviewAssetSet, productConfId: ProductConfId) {
  const values = productConfId.root.smako_conf.column;
  const executionGroupCode = normalizeCode(values.user_grp_prov);
  const executionCode = normalizeCode(values.user_kod_prov);

  return (
    assetSet.configurationMappings.find(
      mapping =>
        mapping.executionGroupCode === executionGroupCode &&
        mapping.executionCode === executionCode,
    ) ?? null
  );
}

function getMappedConfiguration(assetSet: ProformPreviewAssetSet, productConfId: ProductConfId) {
  const mapping = getConfigurationMapping(assetSet, productConfId);
  if (!mapping) return null;

  const model = assetSet.variants.find(variant => variant.variantCode === mapping.modelVariantCode);
  return model ? { mapping, model } : null;
}

export function getProformPreviewModelForExecution(
  assetSet: ProformPreviewAssetSet,
  {
    executionCode,
    executionGroupCode,
    executionLabel,
    previewImagePath,
  }: {
    executionCode: string;
    executionGroupCode: string;
    executionLabel?: string;
    previewImagePath?: string;
  },
) {
  const normalizedExecutionCode = normalizeCode(executionCode);
  const normalizedExecutionGroupCode = normalizeCode(executionGroupCode);
  if (!normalizedExecutionCode) return null;

  // Decorative panels use this same API field for milling continuity, not a
  // door shape. Their NFA/NFN thumbnails must not replace the dimensioned
  // image-generator preview with a lookup for a nonexistent sibling OBJ.
  if (
    !normalizedExecutionGroupCode &&
    (normalizedExecutionCode === 'NFA' || normalizedExecutionCode === 'NFN')
  ) {
    return null;
  }

  const mapping = assetSet.configurationMappings.find(
    candidate =>
      normalizeCode(candidate.executionGroupCode) === normalizedExecutionGroupCode &&
      normalizeCode(candidate.executionCode) === normalizedExecutionCode,
  );
  const fallbackModel = mapping
    ? assetSet.variants.find(variant => variant.variantCode === mapping.modelVariantCode)
    : undefined;
  const resolvedPreviewImagePath = previewImagePath || fallbackModel?.previewImagePath;

  if (fallbackModel) {
    return withPreviewImagePath(fallbackModel, resolvedPreviewImagePath);
  }

  // A dynamically selected execution must point to its own sibling OBJ. Do
  // not reuse P, XMF8, or any other door merely because it is available.
  if (!deriveProformObjPath(resolvedPreviewImagePath)) return null;

  const label = executionLabel?.trim() || normalizedExecutionCode;
  const cacheKey = [
    normalizedExecutionGroupCode,
    normalizedExecutionCode,
    resolvedPreviewImagePath,
    label,
  ].join('\0');
  let models = DYNAMIC_MODEL_CACHE.get(assetSet);
  if (!models) {
    models = new Map();
    DYNAMIC_MODEL_CACHE.set(assetSet, models);
  }

  const cachedModel = models.get(cacheKey);
  if (cachedModel) return cachedModel;

  const model: ProformModelDescriptor = {
    ...(assetSet.edgeRadiusMm ? { edgeRadiusMm: assetSet.edgeRadiusMm } : {}),
    id: `${assetSet.productCode}-${normalizedExecutionGroupCode}-${normalizedExecutionCode}`,
    label: `${assetSet.productCode} · ${normalizedExecutionCode} · ${label}`,
    // The viewer measures the downloaded geometry before applying the actual
    // configured dimensions. These values are metadata-only for dynamic
    // entries and never determine the rendered proportions.
    nativeSizeMm: { width: 400, height: 800, depth: 18 },
    previewImagePath: resolvedPreviewImagePath,
    sources: [],
    variantCode: normalizedExecutionCode,
  };
  models.set(cacheKey, model);
  return model;
}

export function getProformPreviewAssetSet(productCode: string) {
  return PRODUCT_PREVIEW_ASSETS[normalizeCode(productCode)] ?? null;
}

export function getProformPreviewRuntimeAssetSet(productCode: string) {
  const normalizedProductCode = normalizeCode(productCode);
  if (!normalizedProductCode) return null;

  const dedicatedAssetSet = getProformPreviewAssetSet(normalizedProductCode);
  if (dedicatedAssetSet) return dedicatedAssetSet;

  const cachedAssetSet = GENERIC_ASSET_SET_CACHE.get(normalizedProductCode);
  if (cachedAssetSet) return cachedAssetSet;

  const assetSet: ProformPreviewAssetSet = {
    configurationMappings: [],
    defaultVariantCode: '',
    productCode: normalizedProductCode,
    variants: [],
  };
  GENERIC_ASSET_SET_CACHE.set(normalizedProductCode, assetSet);
  return assetSet;
}

export function getProformPreviewModelForConfiguration(
  productCode: string,
  productConfId: ProductConfId,
  productConf?: EnhancedProductConf,
) {
  const values = productConfId.root.smako_conf.column;
  const normalizedProductCode = normalizeCode(productCode);
  const configuredProductCode = normalizeCode(values.cislo_nomenklatury);
  if (
    !normalizedProductCode ||
    (configuredProductCode && configuredProductCode !== normalizedProductCode)
  ) {
    return null;
  }

  const executionGroupCode = normalizeCode(values.user_grp_prov);
  const executionCode = normalizeCode(values.user_kod_prov);
  const dedicatedAssetSet = getProformPreviewAssetSet(productCode);
  const execution = productConf
    ? getSelectedOption(productConf, 'user_kod_prov', executionCode, executionGroupCode)
    : undefined;
  const mappedConfiguration = dedicatedAssetSet
    ? getMappedConfiguration(dedicatedAssetSet, productConfId)
    : null;

  if (!mappedConfiguration && !execution) return null;

  const assetSet = dedicatedAssetSet ?? getProformPreviewRuntimeAssetSet(productCode);
  if (!assetSet) return null;

  return getProformPreviewModelForExecution(assetSet, {
    executionCode,
    executionGroupCode,
    executionLabel: execution?.popis,
    previewImagePath: execution?.pic,
  });
}

export function hasProformPreviewForConfiguration(
  productCode: string,
  productConfId: ProductConfId,
  productConf?: EnhancedProductConf,
) {
  return Boolean(getProformPreviewModelForConfiguration(productCode, productConfId, productConf));
}

export function getProformPreviewVariant(
  assetSet: ProformPreviewAssetSet,
  variantCode = assetSet.defaultVariantCode,
) {
  const normalizedCode = variantCode.trim().toUpperCase();
  return assetSet.variants.find(variant => variant.variantCode === normalizedCode) ?? null;
}

export function getProformPreviewHandle(assetSet: ProformPreviewAssetSet, handleId: string) {
  return assetSet.handles?.find(handle => handle.id === handleId) ?? null;
}

export function isProformPreviewHandleCompatible(
  handle: ProformHandleDescriptor,
  edgeLengthMm: number,
) {
  return (
    Number.isFinite(edgeLengthMm) &&
    edgeLengthMm > 0 &&
    Number.isFinite(handle.lengthMm) &&
    handle.lengthMm > 0 &&
    handle.lengthMm <= edgeLengthMm
  );
}

export function getCompatibleProformPreviewHandles(
  assetSet: ProformPreviewAssetSet,
  edgeLengthMm: number,
) {
  return (assetSet.handles ?? []).filter(handle =>
    isProformPreviewHandleCompatible(handle, edgeLengthMm),
  );
}

export function getSuggestedProformPreviewHandle(
  assetSet: ProformPreviewAssetSet,
  edgeLengthMm: number,
) {
  return (
    getCompatibleProformPreviewHandles(assetSet, edgeLengthMm).sort(
      (a, b) => b.lengthMm - a.lengthMm,
    )[0] ?? null
  );
}

function resolveSelectedEdges(
  values: ProductConfId['root']['smako_conf']['column'],
  productConf: EnhancedProductConf,
): ProformPreviewConfiguration['edges'] {
  const edges: ProformPreviewConfiguration['edges'] = {};
  const options = extractProformArray(productConf.root.smako_conf_hrana?.hrana);
  const columns: Array<[ProformEdgeSide, keyof typeof values]> = [
    ['top', 'user_hrana_h'],
    ['right', 'user_hrana_p'],
    ['bottom', 'user_hrana_d'],
    ['left', 'user_hrana_l'],
  ];

  for (const [side, column] of columns) {
    const code = normalizeCode(values[column]);
    if (!code) continue;
    const matches = options.filter(option => normalizeCode(option.kod) === code);
    if (matches.length !== 1) continue;

    const option = matches[0];
    const pic = option.pic?.trim();
    edges[side] = {
      code,
      label: option.popis?.trim() || code,
      ...(pic
        ? { previewUrl: getProformImageUrl(pic), textureUrl: getProformTextureProxyUrl(pic) }
        : {}),
    };
  }
  return edges;
}

export function resolveProformPreviewConfiguration(
  assetSet: ProformPreviewAssetSet,
  productConfId: ProductConfId,
  productDetail: ProductDetail,
  productConf: EnhancedProductConf,
): ProformPreviewConfiguration | null {
  const values = productConfId.root.smako_conf.column;
  const defaults = productDetail.root.smako_nom_default.default;
  const configuredProductCode = normalizeCode(values.cislo_nomenklatury);
  if (configuredProductCode && configuredProductCode !== normalizeCode(assetSet.productCode)) {
    return null;
  }

  const executionGroupCode = normalizeCode(values.user_grp_prov);
  const executionCode = normalizeCode(values.user_kod_prov);
  const mappedConfiguration = getMappedConfiguration(assetSet, productConfId);
  const executionGroup = getSelectedOption(productConf, 'user_grp_prov', executionGroupCode);
  const execution = getSelectedOption(
    productConf,
    'user_kod_prov',
    executionCode,
    executionGroupCode,
  );
  const model = getProformPreviewModelForExecution(assetSet, {
    executionCode,
    executionGroupCode,
    executionLabel: execution?.popis,
    previewImagePath: execution?.pic,
  });

  if (!model) return null;

  const fillingFlag = normalizeCode(execution?.prov_sklo);
  const supportsFilling = fillingFlag
    ? fillingFlag !== '0'
    : (mappedConfiguration?.mapping.usesFilling ?? false);
  const finishCode = values.user_kod_folie || defaults.user_kod_folie || '';
  const fillingGroupCode = supportsFilling ? normalizeCode(values.user_grp_skla) : '';
  const fillingCode = supportsFilling ? normalizeCode(values.user_kod_skla) : '';
  const widthMm = positiveNumber(values.user_sirka, positiveNumber(defaults.user_sirka, 800));
  const supportsAccessories = normalizeCode(assetSet.productCode) === 'NDALVICZENIT';
  const handleGroupCode = normalizeCode(values.user_grp_pantu);
  const handleCode = normalizeCode(values.user_kod_pantu);
  const handleGroups = (extractDataFromField('user_grp_pantu', productConf) ?? []).filter(
    option => normalizeCode(option.kod) === handleGroupCode,
  );
  const handleOptions = (extractDataFromField('user_kod_pantu', productConf) ?? []).filter(
    option =>
      normalizeCode(option.kod) === handleCode && normalizeCode(option.kod_grp) === handleGroupCode,
  );
  const handlePlacementCode = normalizeCode(values.user_pant_umi);
  const hasSupportedHandlePlacement =
    !handlePlacementCode || handlePlacementCode === 'HS' || handlePlacementCode === 'UGC';
  const hasConfiguredHandleSelection =
    supportsAccessories &&
    Boolean(handleGroupCode && handleCode) &&
    handleGroups.length === 1 &&
    handleOptions.length === 1;
  const configuredHandle =
    hasConfiguredHandleSelection &&
    handleGroupCode === 'UAL-08' &&
    handleCode === 'UAL-8/5' &&
    hasSupportedHandlePlacement
      ? (getSuggestedProformPreviewHandle(assetSet, widthMm) ?? undefined)
      : undefined;

  const finish = getSelectedOption(productConf, 'user_kod_folie', finishCode);
  const fillingGroup = supportsFilling
    ? getSelectedOption(productConf, 'user_grp_skla', fillingGroupCode)
    : undefined;
  const filling = supportsFilling
    ? getSelectedOption(productConf, 'user_kod_skla', fillingCode, fillingGroupCode)
    : undefined;
  // prov_sklo describes the compatible opening layout, not the look of the material.
  // Value 2 is a grille; 1/3/4/5 are glazed layouts. Unknown values fail closed.
  const supportsGlass = fillingFlag
    ? GLASS_EXECUTION_FLAGS.has(fillingFlag)
    : (mappedConfiguration?.mapping.usesFilling ?? false);
  const usesFilling =
    supportsGlass &&
    Boolean(fillingGroup && filling) &&
    !NON_GLASS_FILLING_GROUPS.has(fillingGroupCode);
  const finishSpecification = extractDataFromField('user_folie_spec', productConf)?.find(
    option => option.kod === values.user_folie_spec,
  );
  const finishColor = normalizeHexColor(finishSpecification?.html);
  // Reject stale colours from another RAL/NCS specification family.
  const fillingSpecification =
    usesFilling && filling?.sklo_spec && filling.sklo_spec !== '0'
      ? extractDataFromField('user_sklo_spec', productConf)?.find(
          option =>
            normalizeCode(option.kod) === normalizeCode(values.user_sklo_spec) &&
            normalizeCode(option.sklo_spec) === normalizeCode(filling.sklo_spec),
        )
      : undefined;

  return {
    depthMm: positiveNumber(values.user_sila, positiveNumber(defaults.user_sila, 18)),
    edges: supportsAccessories ? resolveSelectedEdges(values, productConf) : {},
    executionCode,
    executionGroupCode,
    executionGroupLabel: executionGroup?.popis || executionGroupCode,
    executionLabel: execution?.popis || executionCode || model.label,
    fillingCode: fillingCode || undefined,
    fillingColor: normalizeHexColor(fillingSpecification?.html),
    fillingGroupCode: fillingGroupCode || undefined,
    fillingGroupLabel: fillingGroup?.popis || fillingGroupCode || undefined,
    fillingLabel: filling?.popis || fillingCode || undefined,
    fillingOrientation: usesFilling ? normalizeCode(values.user_smer_skla) || undefined : undefined,
    fillingOrnamentSide: usesFilling
      ? normalizeCode(values.user_ornament_skla) || undefined
      : undefined,
    fillingPreviewUrl: filling?.pic ? getProformImageUrl(filling.pic) : undefined,
    finishCode,
    finishColor,
    finishLabel: finish?.popis || finishCode || 'Bez zvolené povrchové úpravy',
    finishPreviewUrl: finish?.pic ? getProformImageUrl(finish.pic) : undefined,
    finishRotationRadians: values.user_smer_let === '20' ? Math.PI / 2 : 0,
    finishTextureUrl:
      !finishColor && finish?.pic ? getProformTextureProxyUrl(finish.pic) : undefined,
    handle: configuredHandle,
    hasConfiguredHandleSelection,
    heightMm: positiveNumber(values.user_vyska, positiveNumber(defaults.user_vyska, 800)),
    model,
    supportsGlass,
    usesFilling,
    variantCode: model.variantCode,
    widthMm,
  };
}
